import React, { useEffect, useRef, useState } from "react";
import Editor from "@monaco-editor/react";
import { AlertCircle, Braces, Plus, Trash2, WandSparkles } from "lucide-react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

export type JsonPrimitive = string | number | boolean | null;
export type JsonValue = JsonPrimitive | JsonObject | JsonValue[];
export type JsonObject = { [key: string]: JsonValue };

type JsonValueType = "string" | "number" | "boolean" | "null" | "object" | "array";

type JsonEditorProps = {
  value: JsonObject;
  onChange: (value: JsonObject) => void;
  onValidityChange?: (valid: boolean) => void;
  disabled?: boolean;
};

const isJsonObject = (value: unknown): value is JsonObject =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const valueType = (value: JsonValue): JsonValueType => {
  if (value === null) return "null";
  if (Array.isArray(value)) return "array";
  if (isJsonObject(value)) return "object";
  return typeof value as "string" | "number" | "boolean";
};

const defaultValue = (type: JsonValueType): JsonValue => {
  switch (type) {
    case "number":
      return 0;
    case "boolean":
      return false;
    case "null":
      return null;
    case "object":
      return {};
    case "array":
      return [];
    default:
      return "";
  }
};

const nextPropertyName = (object: JsonObject) => {
  let index = 1;
  let candidate = "property";
  while (Object.prototype.hasOwnProperty.call(object, candidate)) {
    index += 1;
    candidate = `property_${index}`;
  }
  return candidate;
};

function TypeSelect({
  value,
  onChange,
  disabled,
}: {
  value: JsonValueType;
  onChange: (type: JsonValueType) => void;
  disabled?: boolean;
}) {
  return (
    <Select value={value} onValueChange={(type) => onChange(type as JsonValueType)} disabled={disabled}>
      <SelectTrigger size="sm" className="w-[104px] font-mono text-xs">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="string">string</SelectItem>
        <SelectItem value="number">number</SelectItem>
        <SelectItem value="boolean">boolean</SelectItem>
        <SelectItem value="null">null</SelectItem>
        <SelectItem value="object">object</SelectItem>
        <SelectItem value="array">array</SelectItem>
      </SelectContent>
    </Select>
  );
}

function ObjectKeyInput({
  propertyKey,
  siblingKeys,
  onRename,
  disabled,
}: {
  propertyKey: string;
  siblingKeys: string[];
  onRename: (nextKey: string) => void;
  disabled?: boolean;
}) {
  const [draftKey, setDraftKey] = useState(propertyKey);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => setDraftKey(propertyKey), [propertyKey]);

  const commit = () => {
    if (draftKey !== propertyKey && siblingKeys.includes(draftKey)) {
      setError("That property already exists.");
      return;
    }
    setError(null);
    onRename(draftKey);
  };

  return (
    <div className="min-w-0 flex-1">
      <Input
        value={draftKey}
        onChange={(event) => {
          setDraftKey(event.target.value);
          setError(null);
        }}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            event.currentTarget.blur();
          }
        }}
        aria-label="Property name"
        aria-invalid={!!error}
        className="h-8 font-mono text-xs"
        disabled={disabled}
      />
      {error && <p className="mt-1 text-xs text-destructive">{error}</p>}
    </div>
  );
}

function ValueField({
  value,
  onChange,
  disabled,
}: {
  value: JsonValue;
  onChange: (value: JsonValue) => void;
  disabled?: boolean;
}) {
  if (typeof value === "string") {
    return (
      <Input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        aria-label="String value"
        placeholder="Value"
        className="font-mono text-xs"
        disabled={disabled}
      />
    );
  }

  if (typeof value === "number") {
    return (
      <Input
        type="number"
        value={Number.isFinite(value) ? value : 0}
        onChange={(event) => onChange(event.target.value === "" ? 0 : Number(event.target.value))}
        aria-label="Number value"
        className="font-mono text-xs"
        disabled={disabled}
      />
    );
  }

  if (typeof value === "boolean") {
    return (
      <Select
        value={String(value)}
        onValueChange={(nextValue) => onChange(nextValue === "true")}
        disabled={disabled}
      >
        <SelectTrigger className="w-full font-mono text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="true">true</SelectItem>
          <SelectItem value="false">false</SelectItem>
        </SelectContent>
      </Select>
    );
  }

  if (value === null) {
    return <div className="rounded-md border bg-muted/40 px-3 py-2 font-mono text-xs text-muted-foreground">null</div>;
  }

  if (Array.isArray(value)) {
    return <ArrayFields value={value} onChange={onChange} disabled={disabled} />;
  }

  return <ObjectFields value={value} onChange={onChange} disabled={disabled} nested />;
}

function ObjectFields({
  value,
  onChange,
  disabled,
  nested = false,
}: {
  value: JsonObject;
  onChange: (value: JsonObject) => void;
  disabled?: boolean;
  nested?: boolean;
}) {
  const entries = Object.entries(value);

  const rename = (oldKey: string, newKey: string) => {
    if (oldKey === newKey || Object.prototype.hasOwnProperty.call(value, newKey)) return;

    const renamed = Object.fromEntries(
      entries.map(([key, entryValue]) => [key === oldKey ? newKey : key, entryValue])
    );
    onChange(renamed);
  };

  return (
    <div className={nested ? "rounded-lg border bg-muted/20 p-2" : "space-y-2"}>
      {entries.length === 0 && (
        <div className="rounded-md border border-dashed px-3 py-4 text-center text-xs text-muted-foreground">
          Empty object
        </div>
      )}
      <div className="space-y-2">
        {entries.map(([key, entryValue]) => (
          <div key={key} className="rounded-lg border bg-background p-2 shadow-xs">
            <div className="flex items-start gap-2">
              <ObjectKeyInput
                propertyKey={key}
                siblingKeys={Object.keys(value).filter((candidate) => candidate !== key)}
                onRename={(newKey) => rename(key, newKey)}
                disabled={disabled}
              />
              <TypeSelect
                value={valueType(entryValue)}
                onChange={(type) => onChange({ ...value, [key]: defaultValue(type) })}
                disabled={disabled}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-8 text-muted-foreground hover:text-destructive"
                onClick={() => {
                  const nextValue = { ...value };
                  delete nextValue[key];
                  onChange(nextValue);
                }}
                aria-label={`Remove ${key}`}
                disabled={disabled}
              >
                <Trash2 />
              </Button>
            </div>
            <div className="mt-2">
              <ValueField
                value={entryValue}
                onChange={(nextValue) => onChange({ ...value, [key]: nextValue })}
                disabled={disabled}
              />
            </div>
          </div>
        ))}
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className={nested ? "mt-2" : ""}
        onClick={() => onChange({ ...value, [nextPropertyName(value)]: "" })}
        disabled={disabled}
      >
        <Plus /> Add property
      </Button>
    </div>
  );
}

function ArrayFields({
  value,
  onChange,
  disabled,
}: {
  value: JsonValue[];
  onChange: (value: JsonValue[]) => void;
  disabled?: boolean;
}) {
  return (
    <div className="rounded-lg border bg-muted/20 p-2">
      {value.length === 0 && (
        <div className="rounded-md border border-dashed px-3 py-4 text-center text-xs text-muted-foreground">
          Empty array
        </div>
      )}
      <div className="space-y-2">
        {value.map((item, index) => (
          <div key={index} className="rounded-lg border bg-background p-2 shadow-xs">
            <div className="flex items-center gap-2">
              <span className="w-8 text-center font-mono text-xs text-muted-foreground">{index}</span>
              <TypeSelect
                value={valueType(item)}
                onChange={(type) => {
                  const nextValue = [...value];
                  nextValue[index] = defaultValue(type);
                  onChange(nextValue);
                }}
                disabled={disabled}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="ml-auto size-8 text-muted-foreground hover:text-destructive"
                onClick={() => onChange(value.filter((_, itemIndex) => itemIndex !== index))}
                aria-label={`Remove item ${index + 1}`}
                disabled={disabled}
              >
                <Trash2 />
              </Button>
            </div>
            <div className="mt-2">
              <ValueField
                value={item}
                onChange={(nextItem) => {
                  const nextValue = [...value];
                  nextValue[index] = nextItem;
                  onChange(nextValue);
                }}
                disabled={disabled}
              />
            </div>
          </div>
        ))}
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="mt-2"
        onClick={() => onChange([...value, ""])}
        disabled={disabled}
      >
        <Plus /> Add item
      </Button>
    </div>
  );
}

const parseObject = (source: string): { value?: JsonObject; error?: string } => {
  if (source.trim() === "") return { error: "Metadata cannot be empty. Use {} for an empty object." };

  try {
    const parsed: unknown = JSON.parse(source);
    if (!isJsonObject(parsed)) return { error: "The JSON root must be an object." };
    return { value: parsed };
  } catch (error) {
    return { error: error instanceof Error ? error.message : "Invalid JSON." };
  }
};

export function JsonEditor({ value, onChange, onValidityChange, disabled }: JsonEditorProps) {
  const [mode, setMode] = useState<"ui" | "code">("ui");
  const [code, setCode] = useState(() => JSON.stringify(value, null, 2));
  const [codeError, setCodeError] = useState<string | null>(null);
  const lastEmittedValue = useRef<string | null>(null);

  useEffect(() => {
    const serializedValue = JSON.stringify(value);
    if (lastEmittedValue.current === serializedValue) {
      lastEmittedValue.current = null;
      return;
    }

    setCode(JSON.stringify(value, null, 2));
    setCodeError(null);
  }, [value]);

  useEffect(() => {
    onValidityChange?.(!codeError);
  }, [codeError, onValidityChange]);

  const handleCodeChange = (source = "") => {
    setCode(source);
    const result = parseObject(source);
    setCodeError(result.error || null);
    if (result.value) {
      lastEmittedValue.current = JSON.stringify(result.value);
      onChange(result.value);
    }
  };

  const handleModeChange = (nextMode: string) => {
    if (nextMode === "ui" && codeError) return;
    setMode(nextMode as "ui" | "code");
  };

  return (
    <div className="rounded-lg border bg-card">
      <div className="flex items-center justify-between gap-3 border-b px-3 py-2">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Braces className="size-4" />
          JSON metadata
        </div>
        <Tabs value={mode} onValueChange={handleModeChange}>
          <TabsList className="h-8">
            <TabsTrigger value="ui" className="h-7 px-3 text-xs" disabled={!!codeError}>UI</TabsTrigger>
            <TabsTrigger value="code" className="h-7 px-3 text-xs">Code</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {mode === "ui" ? (
        <div className="p-3">
          <ObjectFields value={value} onChange={onChange} disabled={disabled} />
        </div>
      ) : (
        <div>
          <div className="flex items-center justify-between border-b bg-muted/20 px-3 py-1.5">
            <span className="text-xs text-muted-foreground">JSON object</span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                const result = parseObject(code);
                if (!result.value) return;
                setCode(JSON.stringify(result.value, null, 2));
              }}
              disabled={disabled || !!codeError}
            >
              <WandSparkles /> Format
            </Button>
          </div>
          <Editor
            height="320px"
            language="json"
            value={code}
            onChange={handleCodeChange}
            theme="vs-dark"
            options={{
              automaticLayout: true,
              folding: true,
              fontSize: 13,
              formatOnPaste: true,
              formatOnType: true,
              lineNumbers: "on",
              minimap: { enabled: false },
              padding: { top: 12, bottom: 12 },
              readOnly: disabled,
              scrollBeyondLastLine: false,
              tabSize: 2,
              wordWrap: "on",
            }}
          />
        </div>
      )}

      {codeError && (
        <div className="border-t p-3">
          <Alert variant="destructive">
            <AlertCircle />
            <AlertDescription>
              {codeError} Fix the JSON before returning to UI mode or saving.
            </AlertDescription>
          </Alert>
        </div>
      )}
    </div>
  );
}
